export interface StoreListItem {
  id: number;
  name: string;
  type: string;
  currency: string;
  owner: { email: string; displayName: string | null } | null;
  integrations: string[];
}

// Matches GET /api/v1/ingestion/admin/halo-diagnostics response shape
export interface HaloDiagnostics {
  storeId: number;
  dateRange: { startDate: string; endDate: string };
  baseline: {
    rowCount: number;
    minDate: string | null;
    maxDate: string | null;
  };
  attribution: {
    rowCount: number;
    minDate: string | null;
    maxDate: string | null;
  };
}

// Matches GET /api/v1/halo/settings response shape
export interface HaloSettings {
  storeId: number;
  cpmRate: number;
  v2Features?: Record<string, boolean>;
}

export interface ComputationResult {
  computation: string;
  rowCount: number;
  status: 'success' | 'error';
  error?: string;
}

export type ComputationType = 'baseline' | 'attribution' | 'rollup' | 'correlation' | 'multiSignal';

export const COMPUTATION_OPTIONS: readonly { readonly key: ComputationType; readonly label: string }[] = [
  { key: 'baseline', label: 'Baseline' },
  { key: 'attribution', label: 'Attribution' },
  { key: 'rollup', label: 'Rollup' },
  { key: 'correlation', label: 'Correlation' },
  { key: 'multiSignal', label: 'Multi-Signal' },
] as const;

// ─── Platform Mappings ──────────────────────────

export const PLATFORM_LABELS: Record<string, string> = {
  shopify: 'Shopify',
  meta: 'Meta Ads',
  google_ads: 'Google Ads',
  tiktok_business: 'TikTok',
  tiktok_shop: 'TikTok Shop',
  pinterest: 'Pinterest',
  klaviyo: 'Klaviyo',
  amazon_ads: 'Amazon Ads',
  amazon_seller: 'Amazon Seller',
  ga4: 'GA4',
};

// ─── Helpers ────────────────────────────────────

export function formatDateShort(d: string): string {
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0] ?? '';
}

export function today(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

export function safeLocaleNumber(val: number | undefined | null): string {
  if (val === undefined || val === null || isNaN(val)) return '0';
  return Number(val).toLocaleString();
}
